import { FC, useMemo } from 'react';
import SwitchList from '../../switch-list';
import { FormProps } from '../../helpers';
import { UiOption } from '@/lib/helpers';
import {
  getSensitiveDataTypeOptions,
  SensitiveDataCategoryOptions,
  SensitiveDataEntity,
} from '@/lib/models/policy-model-v1/sensitive-data';
import { cloneDeep } from 'lodash';
import useUserControl from '@/hooks/use-user-control.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import useDrawer from '@/hooks/use-drawer.hook';

interface Props extends FormProps {
  value: SensitiveDataEntity[];
  onChange: (entities: SensitiveDataEntity[]) => void;
  policy: PolicyModelV1;
  orgPolicy?: PolicyModelV1;
}

const SdEntityListV1: FC<Props> = ({
  value: entities,
  onChange,
  policy,
  orgPolicy,
  ...rest
}) => {
  const { openDrawer } = useDrawer();
  const { checkLockedAndEmit } = useUserControl();

  const value = useMemo(() => {
    return entities.filter(({ enabled }) => enabled).map(({ type }) => type);
  }, [entities]);

  const options = useMemo(() => {
    return getSensitiveDataTypeOptions(policy, orgPolicy);
  }, [policy, orgPolicy]);

  const props = useMemo(
    () => ({
      ...rest,
      value,
      onChange: (types: string[]) => {
        const updated = cloneDeep(entities);

        options.forEach(({ value: entityType }) => {
          const enabled = types.includes(entityType);
          const entityIndex = updated.findIndex(({ type }) => type === entityType);
          if (entityIndex > -1) {
            updated[entityIndex].enabled = enabled;
          } else {
            updated.push({
              type: entityType,
              enabled,
              scanType: 1,
            });
          }
        });

        onChange(updated);
      },
    }),
    [entities, onChange, value, rest, options]
  );

  const handleOpenDrawer = (opt: UiOption) => {
    if (checkLockedAndEmit()) {
      return;
    }
    const { value } = opt;
    openDrawer('sd-entity', {
      id: value,
    });
  };

  return (
    <SwitchList
      {...props}
      options={options}
      categoryOptions={SensitiveDataCategoryOptions}
      height={400}
      search
      onSettings={handleOpenDrawer}
    />
  );
};

export default SdEntityListV1;
